import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';

/**
 * `WishlistItem` — JWT-customerId-scoped saved-products list. Mirrors
 * BACKEND_AGENT_PROMPTS.md Item 4. The list endpoint joins to a thin
 * product projection so `<AccountWishlist>` doesn't need a per-row
 * product fetch.
 */
export interface WishlistItem {
  wishlistItemId?: string;
  productId: string;
  /** Server-joined snapshot — present on `mine()` results only. */
  productName?: string;
  productSellingPrice?: number;
  productImageUrl?: string;
  productIsActive?: boolean;
  createdAt?: string;
}

export interface WishlistToggleResult {
  added: boolean;
  total: number;
}

export interface WishlistService {
  /** `POST /api/auth/me/wishlist` — list this customer's saved items. */
  mine(): Promise<WishlistItem[]>;
  /**
   * `POST /api/auth/me/wishlist/Toggle { ProductId }` — add if missing,
   * remove if present. Returns the new state + total count so the
   * heart-icon button can update without a re-fetch.
   */
  toggle(productId: string): Promise<WishlistToggleResult>;
  /** `POST /api/auth/me/wishlist/Delete { WishlistItemId }`. */
  remove(wishlistItemId: string): Promise<void>;
}

export function createWishlistService(client: AxiosInstance): WishlistService {
  return {
    mine: () =>
      client
        .post<ObjectResponse<WishlistItem[] | { items?: WishlistItem[] }>>(
          '/auth/me/wishlist',
          {},
        )
        .then((r) => unwrapItems<WishlistItem>(r.data)),

    toggle: (productId) =>
      client
        .post<ObjectResponse<WishlistToggleResult>>('/auth/me/wishlist/Toggle', {
          ProductId: productId,
        })
        .then((r) => unwrap(r.data)),

    remove: (wishlistItemId) =>
      client
        .post<ObjectResponse<null>>('/auth/me/wishlist/Delete', {
          WishlistItemId: wishlistItemId,
        })
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
